/**
 * IndexedDB Model Cache for Helios Browser AI
 *
 * Caches ONNX models locally in IndexedDB so they only need
 * to be downloaded once. Models persist across browser sessions.
 */

const DB_NAME = 'helios-model-cache';
const DB_VERSION = 1;
const STORE_NAME = 'models';

interface CachedModel {
  id: string;
  data: ArrayBuffer;
  size: number;
  cached_at: string;
}

export class ModelCache {
  private db: IDBDatabase | null = null;

  private async openDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onerror = () => {
        reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`));
      };
    });
  }

  async getModel(modelId: string): Promise<ArrayBuffer | null> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(modelId);

        request.onsuccess = () => {
          const result = request.result as CachedModel | undefined;
          resolve(result?.data ?? null);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn('[ModelCache] Failed to read from cache:', err);
      return null;
    }
  }

  async saveModel(modelId: string, data: ArrayBuffer): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const entry: CachedModel = {
          id: modelId,
          data,
          size: data.byteLength,
          cached_at: new Date().toISOString(),
        };

        const request = store.put(entry);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn('[ModelCache] Failed to save to cache:', err);
    }
  }

  async hasModel(modelId: string): Promise<boolean> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.count(modelId);

        request.onsuccess = () => resolve(request.result > 0);
        request.onerror = () => reject(request.error);
      });
    } catch {
      return false;
    }
  }

  async listModels(): Promise<Array<{ id: string; size: number; cached_at: string }>> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          const entries = (request.result as CachedModel[]) || [];
          resolve(
            entries.map((e) => ({
              id: e.id,
              size: e.size,
              cached_at: e.cached_at,
            }))
          );
        };

        request.onerror = () => reject(request.error);
      });
    } catch {
      return [];
    }
  }

  async clearCache(): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          console.log('[ModelCache] Cache cleared');
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn('[ModelCache] Failed to clear cache:', err);
    }
  }

  async deleteModel(modelId: string): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.delete(modelId);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.warn('[ModelCache] Failed to delete model:', err);
    }
  }

  async getCacheSize(): Promise<number> {
    try {
      const models = await this.listModels();
      return models.reduce((total, m) => total + m.size, 0);
    } catch {
      return 0;
    }
  }
}
